// The catch-all route.
//
// The text is the smaller half of what was wrong here. The sidebar and the
// breadcrumb both derive from the pathname, so /sessions/test used to light up
// "Live sessions" and render "Dashboard / Live sessions / test" above a page
// saying it did not exist: chrome that insists the operator is somewhere real
// reads as a broken page rather than a wrong URL. `useShellPathname` points the
// shell at NOT_FOUND_PATH, which matches no nav item and carries its own
// breadcrumb label, for as long as this page is mounted.
//
// The address that missed is printed rather than described, because the two
// ways to arrive here (a typo and a stale link) are both diagnosed by reading
// it back.

import { Button } from "@veil/ui/components/button";
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@veil/ui/components/empty";
import { Kbd } from "@veil/ui/components/kbd";
import RouteOff from "lucide-react/dist/esm/icons/route-off";
import Search from "lucide-react/dist/esm/icons/search";
import { Link, useLocation, useSearch } from "wouter";
import {
	openCommandPalette,
	SHORTCUT_LABEL,
} from "@/components/command-palette";
import { NOT_FOUND_PATH } from "@/lib/nav";
import { useShellPathname } from "@/lib/shell-chrome";

export function NotFound() {
	const [location] = useLocation();
	const search = useSearch();
	useShellPathname(NOT_FOUND_PATH);

	const requested = search ? `${location}?${search}` : location;

	return (
		<Empty className="border">
			<EmptyHeader>
				<EmptyMedia variant="icon">
					<RouteOff />
				</EmptyMedia>
				<EmptyTitle>No page at this address</EmptyTitle>
				<EmptyDescription>
					Cluster Manager has no route for the address below. Nothing failed on
					the cluster: the address is either out of date or a character off.
				</EmptyDescription>
			</EmptyHeader>
			<EmptyContent>
				<code className="w-full rounded-md bg-muted px-3 py-2 font-mono text-xs break-all text-foreground">
					{requested}
				</code>
				<div className="flex flex-wrap items-center justify-center gap-2">
					<Button asChild size="sm">
						<Link href="/">Back to Dashboard</Link>
					</Button>
					<Button variant="outline" size="sm" onClick={openCommandPalette}>
						<Search />
						Search pages
						<Kbd>{SHORTCUT_LABEL}</Kbd>
					</Button>
				</div>
			</EmptyContent>
		</Empty>
	);
}
